import { useMemo, useCallback } from 'react';
import {
  useAppSelector,
  useComparisonPeriod,
  ComparisonPeriod,
  useMetricFormatter,
  useWeekRange,
  WeekRange
} from 'src/utils/Hooks';
import { removeDspClicks } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBaseMetricRequestBuilder';
import { onDisplayAdvertisingMode } from 'src/store/modules/user/selectors';
import useGenericAdvancedSearch, { UseGenericAdvancedSearchParameters } from 'src/utils/Hooks/useGenericAdvancedSearch';
import { AdvancedSearchByWeekIdResponse } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/types';
import { calculatePercentChange, getSearchAppName } from 'src/utils/app';
import { INDEX_FIELDS, METRICTYPE } from 'src/utils/entityDefinitions';
import AdvancedSearchRequestBuilder, {
  useAdvancedSearchRequestBuilder
} from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';
import AggregationBuilder from 'src/components/BeaconV2Components/utils/AggregationBuilder';
import {
  dayIdToTimestamp,
  getFirstWeekIdOfYear,
  getNextYearDayId,
  getNextYearWeekId,
  getPreviousWeekId,
  getPreviousYearWeekId,
  timestampToWeekId,
  weekIdToTimestamp,
  yearPartOfWeekId,
  getPreviousDayId
} from 'src/utils/dateUtils';
import { ValueOf } from 'sl-api-connector';
import _get from 'lodash/get';
import {
  useApplyEntityFilters,
  useRemoveRetailPriceRangeFilters
} from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBaseMetricRequestBuilder';
import { BEACON_TABS } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';

export interface UseMetricByWeekIdArgs {
  byDayId?: boolean;
  indexName: string;
  fieldName: string;
  comparisonIndexName?: string;
  comparisonFieldName?: string;
  /**
   * True if we want to shift the prior year comparison
   * data up to match the current year
   */
  isComparisonDataCustom?: boolean;
  entityType?: string;
  /**
   * Will use most recent week's data instead of full time period
   */
  useLatestWeek?: boolean;
  /**
   * Allows optinonally modifying the primary aggregation builder
   * before the request is fired
   */
  primaryAggregationBuilder?: (aggregationBuilder: AggregationBuilder) => AggregationBuilder;
  /**
   * Allows optinonally modifying the secondary aggregation builder
   * before the request is fired
   */
  secondaryAggregationBuilder?: (aggregationBuilder: AggregationBuilder) => AggregationBuilder;

  requestBuilder?: (requestBuilder: AdvancedSearchRequestBuilder, comparison: boolean) => AdvancedSearchRequestBuilder;
  /**
   * Sometimes we want to display the metric differently than the default
   */
  metricTypeDisplay?: ValueOf<typeof METRICTYPE>;

  /**
   * Sometimes we want to compute the total value differently than the default
   */
  aggregationFunctionForTotal?: string;

  /**
   * Allows optinonally modifying the data set before it is used
   * to compute the total value
   */
  buildDataSetForMetricTotal?: (
    defaultDataSet: number[][],
    indexNameToUse,
    fieldNameToUse,
    isComparison: boolean
  ) => number[][];

  /**
   * Typically we compare YTD data against comparison up until the current week.
   * This allows a custom comparator function if needed.
   */
  customFilterComparisonForTotal?: (row: number[]) => boolean;

  useGenericAdvancedSearchArgs?: Partial<UseGenericAdvancedSearchParameters>;
}

type TDatum<TAdditionalValues> = TAdditionalValues & {
  weekId: string;
};

/**
 * helper function to get a field by index name and field name
 */
const getField = (indexName: string, fieldName: string, entityType?: string) => {
  return INDEX_FIELDS.getField(getSearchAppName(), indexName, fieldName, entityType);
};

/**
 * Get the key from the response
 */
const getMetricKey = (indexName: string, fieldName: string, entityType?: string) => {
  const field = getField(indexName, fieldName, entityType);
  const keyEnding = field.aggregationFunction === 'stats' ? 'avg' : 'value';
  return `${fieldName}_${field.aggregationFunction}_${keyEnding}`;
};

/**
 * Returns data formatted as a 2D array with x and y values
 * (x as a unix timestamp) for displaying in a spline chart
 */
const getSplineData = (
  indexName: string,
  fieldName: string,
  data: any[],
  groupBy: string,
  entityType?: string,
  byDayId?: boolean
): number[][] => {
  return [...data]
    .sort((a, b) => a[groupBy] - b[groupBy])
    .map((row) => [
      byDayId ? dayIdToTimestamp(Number(row.dayId)) : weekIdToTimestamp(Number(row.weekId)),
      row[getMetricKey(indexName, fieldName, entityType)]
    ]);
};

/**
 * Gets all the spline chart data and legend data given an index name and field name in order
 * to display a spline chart
 */
export const useMetricByWeekId = <TAdditionalValues>({
  indexName,
  fieldName,
  comparisonFieldName,
  comparisonIndexName,
  entityType,
  primaryAggregationBuilder,
  secondaryAggregationBuilder,
  metricTypeDisplay,
  aggregationFunctionForTotal,
  requestBuilder,
  useLatestWeek = false,
  isComparisonDataCustom,
  buildDataSetForMetricTotal,
  customFilterComparisonForTotal,
  useGenericAdvancedSearchArgs = {},
  byDayId
}: UseMetricByWeekIdArgs) => {
  const applyEntityFilters = useApplyEntityFilters();
  const removeRetailPriceRangeFilters = useRemoveRetailPriceRangeFilters(indexName);
  const formatMetric = useMetricFormatter();
  const isAdvertisingDisplayMode = useAppSelector(onDisplayAdvertisingMode);
  const comparisonPeriod = useComparisonPeriod();
  const weekRange = useWeekRange();
  const { queryParams } = useAppSelector((state) => state.app);
  const { tab } = queryParams;

  // Special case, when comparing last week with prior year we want
  // to show the graph for YTD but the legend should just be last week
  // when calculating forecast total, we should not bring this condition(forecast does not have last week condition - and it bypasses all forecastArgs)
  const isLastWeekWithPriorYear = useMemo(() => {
    return weekRange === WeekRange.LAST_WEEK && comparisonPeriod === ComparisonPeriod.PRIOR_YEAR;
  }, [comparisonPeriod, weekRange]);

  const defaultStartWeekId = useAppSelector((state) => state.mainTimePeriod.startWeek);
  const endWeekId = useAppSelector((state) => state.mainTimePeriod.endWeek);
  const startDayId = useAppSelector((state) => state.mainTimePeriod.startDayId);
  const endDayId = useAppSelector((state) => state.mainTimePeriod.endDayId);
  const comparisonStartDayId = useAppSelector((state) => state.comparisonTimePeriod.startDayId);
  const defaultComparisonEndDayId = useAppSelector((state) => state.comparisonTimePeriod.endDayId);

  const startWeekId = useMemo(() => {
    if (useLatestWeek) {
      return endWeekId;
    }
    return isLastWeekWithPriorYear ? getFirstWeekIdOfYear(yearPartOfWeekId(endWeekId)) : defaultStartWeekId;
  }, [defaultStartWeekId, endWeekId, isLastWeekWithPriorYear, useLatestWeek]);

  const defaultComparisonStartWeekId = useAppSelector((state) => state.comparisonTimePeriod.startWeek);
  const defaultComparisonEndWeekId = useAppSelector((state) => state.comparisonTimePeriod.endWeek);

  const comparisonStartWeekId = useMemo(
    () => (isLastWeekWithPriorYear ? getPreviousYearWeekId(startWeekId) : defaultComparisonStartWeekId),
    [defaultComparisonStartWeekId, isLastWeekWithPriorYear, startWeekId]
  );

  const isYTDMainPeriodAndPriorYearComparison = useMemo(() => {
    return weekRange === WeekRange.YTD && comparisonPeriod === ComparisonPeriod.PRIOR_YEAR;
  }, [comparisonPeriod, weekRange]);

  const comparisonEndDayId = useMemo(
    () => (isYTDMainPeriodAndPriorYearComparison ? getPreviousDayId(startDayId) : defaultComparisonEndDayId),
    [defaultComparisonEndDayId, startDayId, isYTDMainPeriodAndPriorYearComparison]
  );

  const comparisonEndWeekId = useMemo(
    () =>
      isLastWeekWithPriorYear || isYTDMainPeriodAndPriorYearComparison
        ? getPreviousWeekId(startWeekId)
        : defaultComparisonEndWeekId,
    [defaultComparisonEndWeekId, isLastWeekWithPriorYear, isYTDMainPeriodAndPriorYearComparison, startWeekId]
  );

  const entity = useAppSelector((state) => state?.entityService?.mainEntity);
  const requestIdWithEntityInfo = `useMetricByWeekId-${indexName}-${fieldName}-${entity?.type}-${entity?.id}`;

  const retailerId = useAppSelector((state) => state.retailer.id);

  const field = useMemo(
    () => INDEX_FIELDS.getField(getSearchAppName(), indexName, fieldName, entityType),
    [indexName, fieldName, entityType]
  );

  const baseRequestBuilderFromHook = useAdvancedSearchRequestBuilder(
    requestIdWithEntityInfo,
    `${getSearchAppName()}-${indexName}`
  );

  const baseRequestBuilder = useMemo(() => {
    let build = baseRequestBuilderFromHook
      .setDoAggregation(true)
      .setPageNumber(1)
      .setPageSize(1200)
      .setPeriod('year')
      .setReturnDocuments(false)
      .setSearchBy('parent');

    if (!isAdvertisingDisplayMode) {
      build = removeDspClicks(build);
    }

    return build;
  }, [baseRequestBuilderFromHook, isAdvertisingDisplayMode]);

  const baseAggregationBuilder = useMemo(() => {
    if (byDayId) {
      return new AggregationBuilder('dayId').addConditionTermFilter('retailerId', [retailerId]);
    }
    return new AggregationBuilder('weekId').addConditionTermFilter('retailerId', [retailerId]);
  }, [byDayId, retailerId]);

  /** ************** Actual and Comparison Request Builders ***************** */
  const mainAggregationBuilder = useMemo(() => {
    const aggBuilder = baseAggregationBuilder
      .clone()
      .addAggregationField(
        field.displayName,
        field.name,
        field.aggregationFunction,
        true,
        field.aggregationFunctionExpression
      );

    if (byDayId) {
      aggBuilder.addConditionRangeFilter('dayId', startDayId, endDayId);
    } else {
      aggBuilder.addConditionRangeFilter('weekId', startWeekId, endWeekId);
    }

    if (!isAdvertisingDisplayMode) {
      aggBuilder.addConditionTermFilter('searchTerm', ['[DSP Clicks]'], 'must_not');
    }

    if (field.dependentFields) {
      field.dependentFields.forEach((dependentField) => {
        aggBuilder.addAggregationField(
          dependentField.displayName,
          dependentField.name,
          dependentField.aggregationFunction,
          true
        );
      });
    }

    if (primaryAggregationBuilder) {
      return primaryAggregationBuilder(aggBuilder.clone());
    }

    return aggBuilder;
  }, [
    baseAggregationBuilder,
    field.displayName,
    field.name,
    field.aggregationFunction,
    field.aggregationFunctionExpression,
    field.dependentFields,
    byDayId,
    primaryAggregationBuilder,
    isAdvertisingDisplayMode,
    startDayId,
    endDayId,
    startWeekId,
    endWeekId
  ]);

  const mainRequestBody = useMemo(() => {
    const body = baseRequestBuilder
      .clone()
      .addAggregation(mainAggregationBuilder.build())

      .apply(applyEntityFilters)
      .apply(removeRetailPriceRangeFilters);

    if (byDayId) {
      body.addConditionRangeFilter('dayId', startDayId, endDayId);
    } else {
      body.addConditionRangeFilter('weekId', startWeekId, endWeekId);
    }

    if (requestBuilder) {
      return requestBuilder(body, false).build();
    }

    return body.build();
  }, [
    requestBuilder,
    baseRequestBuilder,
    mainAggregationBuilder,
    applyEntityFilters,
    removeRetailPriceRangeFilters,
    byDayId,
    startDayId,
    endDayId,
    startWeekId,
    endWeekId
  ]);

  const comparisonAggregationBuilder = useMemo(() => {
    const aggBuilder = baseAggregationBuilder
      .clone()
      .addAggregationField(
        field.displayName,
        field.name,
        field.aggregationFunction,
        true,
        field.aggregationFunctionExpression
      );

    if (byDayId) {
      aggBuilder.addConditionRangeFilter('dayId', comparisonStartDayId, comparisonEndDayId);
    } else {
      aggBuilder.addConditionRangeFilter('weekId', comparisonStartWeekId, comparisonEndWeekId);
    }

    if (field.dependentFields) {
      field.dependentFields.forEach((dependentField) => {
        aggBuilder.addAggregationField(
          dependentField.displayName,
          dependentField.name,
          dependentField.aggregationFunction,
          true
        );
      });
    }

    if (secondaryAggregationBuilder) {
      return secondaryAggregationBuilder(aggBuilder.clone());
    }

    return aggBuilder;
  }, [
    baseAggregationBuilder,
    field.displayName,
    field.name,
    field.aggregationFunction,
    field.aggregationFunctionExpression,
    field.dependentFields,
    byDayId,
    secondaryAggregationBuilder,
    comparisonStartDayId,
    comparisonEndDayId,
    comparisonStartWeekId,
    comparisonEndWeekId
  ]);

  const comparisonRequestBody = useMemo(() => {
    const body = baseRequestBuilder
      .clone()
      .addAggregation(comparisonAggregationBuilder.build())
      .apply(applyEntityFilters)
      .apply(removeRetailPriceRangeFilters);

    if (byDayId) {
      body.addConditionRangeFilter('dayId', comparisonStartDayId, comparisonEndDayId);
    } else {
      body.addConditionRangeFilter('weekId', comparisonStartWeekId, comparisonEndWeekId);
    }
    if (!isAdvertisingDisplayMode) {
      body.addConditionTermFilter('searchTerm', 'must_not', ['[DSP Clicks]']);
    }

    if (requestBuilder) {
      return requestBuilder(body, true).build();
    }
    return body.build();
  }, [
    isAdvertisingDisplayMode,
    baseRequestBuilder,
    comparisonAggregationBuilder,
    applyEntityFilters,
    removeRetailPriceRangeFilters,
    byDayId,
    requestBuilder,
    comparisonStartDayId,
    comparisonEndDayId,
    comparisonStartWeekId,
    comparisonEndWeekId
  ]);

  const { data: response, isLoading } = useGenericAdvancedSearch<AdvancedSearchByWeekIdResponse<TAdditionalValues[]>>({
    requestBody: [mainRequestBody, comparisonRequestBody],
    requestId: requestIdWithEntityInfo,
    queryKeys: [mainRequestBody, comparisonRequestBody],
    ...useGenericAdvancedSearchArgs
  });

  const mainRows: (TAdditionalValues & { weekId?: number; dayId?: number })[] = useMemo(() => {
    const groupByFieldName = byDayId ? 'by_dayId' : 'by_weekId';
    return response && response.data && response.data[0]
      ? response.data[0].aggregations?.[groupByFieldName].map(({ fieldId, additionalValues }) => {
          if (byDayId) {
            return {
              dayId: fieldId,
              ...additionalValues
            };
          } else {
            return {
              weekId: fieldId,
              ...additionalValues
            };
          }
        })
      : [];
  }, [response, byDayId]);

  const comparisonRows: (TAdditionalValues & { weekId?: number; dayId?: number })[] = useMemo(() => {
    const groupByFieldName = byDayId ? 'by_dayId' : 'by_weekId';
    return response && response.data && response.data[1]
      ? response.data[1].aggregations?.[groupByFieldName].map(({ fieldId, additionalValues }) => {
          if (byDayId) {
            return {
              dayId: fieldId,
              ...additionalValues
            };
          } else {
            return {
              weekId: fieldId,
              ...additionalValues
            };
          }
        })
      : [];
  }, [response, byDayId]);

  /* *************** Helper functions for computing legend values ************************ */
  /**
   * Get the total computed value for a metric. It will be a sum,
   * average, or computation based on the index field
   */
  const getMetricTotalValue = useCallback(
    (indexNameToUse: string, fieldNameToUse: string, comparison = false, aggFunction?: string) => {
      const fieldToUse = getField(indexNameToUse, fieldNameToUse, entityType);
      // Get the primary data set week ids in order
      // so we can compare against comparison data set
      const mainWeekIds = mainRows.map((row) => Number(row.weekId)).sort((a, b) => a - b);
      const defaultDataSet = comparison
        ? getSplineData(
            indexNameToUse,
            fieldNameToUse,
            comparisonRows,
            byDayId ? 'dayId' : 'weekId',
            entityType,
            byDayId
          ).filter((row) => {
            if (customFilterComparisonForTotal) {
              return customFilterComparisonForTotal(row);
            }

            if (byDayId) {
              return true;
            }
            // If we're comparing YTD against prior year, only compare against the same weeks
            if (
              !isComparisonDataCustom &&
              comparisonPeriod === ComparisonPeriod.PRIOR_YEAR &&
              weekRange === WeekRange.YTD
            ) {
              return mainWeekIds.includes(getNextYearWeekId(timestampToWeekId(row[0])));
            }
            return true;
          })
        : getSplineData(indexNameToUse, fieldNameToUse, mainRows, byDayId ? 'dayId' : 'weekId', entityType, byDayId);

      const dataSet = buildDataSetForMetricTotal
        ? buildDataSetForMetricTotal(defaultDataSet, indexNameToUse, fieldNameToUse, comparison)
        : defaultDataSet;

      const aggregationFunctionToUse = aggFunction || fieldToUse.aggregationFunction;

      // When last week with prior year, we're showing YTD data
      // but the legend should just show the last week value, which
      // will be the last item in the data set. We also show
      // the last value for cardinality metric types
      if (
        (isLastWeekWithPriorYear || aggregationFunctionToUse === 'cardinality') &&
        dataSet.length > 0 &&
        tab !== 'forecasts' && // forecast does not have last week filter
        tab !== BEACON_TABS.ADVERTISING
      ) {
        // Get the last week of comparison matching the last week of the main data set
        if (comparison) {
          const lastWeekId = mainWeekIds[mainWeekIds.length - 1];
          const lastComparisonRow = dataSet.find(
            (row) =>
              (isComparisonDataCustom ? timestampToWeekId(row[0]) : getNextYearWeekId(timestampToWeekId(row[0]))) ===
              lastWeekId
          );
          return lastComparisonRow ? lastComparisonRow[1] : 0;
        }
        return dataSet[dataSet.length - 1][1];
      }

      const total = dataSet.reduce((acc, row) => acc + row[1], 0);

      if (aggregationFunctionToUse === 'avg' || aggregationFunctionToUse === 'stats') {
        return dataSet.length > 0 ? total / dataSet.length : 0;
      }

      // Computed index fields have an function evaluator that take
      // variables to compute the total. We should already have the total
      // values for the variables it relies on, so get the values
      // and use them to compute the total
      if (aggregationFunctionToUse === 'computed') {
        const dependencies = fieldToUse.dependentFields;
        const variableValues = dependencies.reduce((acc, depField) => {
          return {
            ...acc,
            [depField.nameAlias || depField.name]: getMetricTotalValue(depField.indexName, depField.name, comparison)
          };
        }, {} as Record<string, number>);
        return fieldToUse.aggregationFunctionEvaluator.evaluate(variableValues);
      }

      return total;
    },
    [
      buildDataSetForMetricTotal,
      byDayId,
      comparisonPeriod,
      comparisonRows,
      customFilterComparisonForTotal,
      entityType,
      isComparisonDataCustom,
      isLastWeekWithPriorYear,
      mainRows,
      tab,
      weekRange
    ]
  );

  const lastSecondaryValue = useMemo(() => {
    const fieldNameToUse = byDayId ? 'dayId' : 'weekId';
    const lastBucket = comparisonRows.find(
      (w) => w?.[fieldNameToUse].toString() === (byDayId ? endDayId : defaultComparisonEndWeekId).toString()
    );

    if (lastBucket) {
      return lastBucket[getMetricKey(indexName, fieldName, entityType)];
    }

    return null;
  }, [comparisonRows, defaultComparisonEndWeekId, entityType, fieldName, indexName, byDayId, endDayId]);

  return useMemo(() => {
    const secondaryData = getSplineData(
      comparisonIndexName || indexName,
      comparisonFieldName || fieldName,
      comparisonRows.map((row) => ({
        ...row,
        dayId:
          !isComparisonDataCustom && comparisonPeriod === ComparisonPeriod.PRIOR_YEAR
            ? getNextYearDayId(row?.dayId)
            : row.dayId,
        weekId:
          !isComparisonDataCustom && comparisonPeriod === ComparisonPeriod.PRIOR_YEAR
            ? getNextYearWeekId(row.weekId)
            : row.weekId
      })),
      byDayId ? 'dayId' : 'weekId',
      entityType,
      byDayId
    );

    // Get last data point
    const basePrimaryData = getSplineData(
      indexName,
      fieldName,
      mainRows,
      byDayId ? 'dayId' : 'weekId',
      entityType,
      byDayId
    );
    const lastPrimaryValue = _get(basePrimaryData[basePrimaryData.length - 1], ['1']);

    // The first element is the epoch timestamp and the 2nd is the value

    const percentChangeLastValues = calculatePercentChange(lastPrimaryValue, lastSecondaryValue);

    const percentChange = calculatePercentChange(
      getMetricTotalValue(indexName, fieldName, false, aggregationFunctionForTotal),
      getMetricTotalValue(indexName, fieldName, true, aggregationFunctionForTotal)
    );

    const metricType = metricTypeDisplay || field.metricType;
    const comparisonField = INDEX_FIELDS.getField(
      getSearchAppName(),
      comparisonIndexName || indexName,
      comparisonFieldName || fieldName,
      entityType
    );
    const comparisonMetricType = comparisonField.metricType;

    return {
      primaryData: [
        ...(!isComparisonDataCustom && comparisonPeriod === ComparisonPeriod.PRIOR_PERIOD && secondaryData.length > 0
          ? [secondaryData[secondaryData.length - 1]]
          : []),
        ...basePrimaryData
      ],
      secondaryData,
      lastPrimaryValue,
      lastSecondaryValue,
      primaryTotal: getMetricTotalValue(indexName, fieldName, false, aggregationFunctionForTotal),
      secondaryTotal: getMetricTotalValue(
        comparisonIndexName || indexName,
        comparisonFieldName || fieldName,
        true,
        aggregationFunctionForTotal
      ),
      formattedPrimaryTotal: `${formatMetric(
        getMetricTotalValue(indexName, fieldName, false, aggregationFunctionForTotal),
        metricType,
        {
          showFullValue: field.metricType === METRICTYPE.PERCENT,
          decimalPlaces: 2,
          showNegative: true
        }
      )}`,
      formattedLastPrimaryValue: `${formatMetric(lastPrimaryValue, metricType, {
        showFullValue: field.metricType === METRICTYPE.PERCENT,
        decimalPlaces: 2,
        showNegative: true
      })}`,
      formattedLastSecondaryValue: `${formatMetric(lastSecondaryValue, comparisonMetricType, {
        showFullValue: comparisonField.metricType === METRICTYPE.PERCENT,
        decimalPlaces: 2,
        showNegative: true
      })}`,
      percentChangeLastValues,
      formattedPercentChangeLastValues: `${formatMetric(Math.abs(percentChangeLastValues), METRICTYPE.PERCENT, {
        decimalPlaces: 2
      })}`,
      formattedSecondaryTotal: `${formatMetric(
        getMetricTotalValue(
          comparisonIndexName || indexName,
          comparisonFieldName || fieldName,
          true,
          aggregationFunctionForTotal
        ),
        comparisonMetricType,
        {
          showFullValue: comparisonField.metricType === METRICTYPE.PERCENT,
          decimalPlaces: 2,
          showNegative: true
        }
      )}`,
      response,
      percentChange,
      formattedPercentChange: `${formatMetric(Math.abs(percentChange), METRICTYPE.PERCENT, { decimalPlaces: 2 })}`,
      isLoading,
      field
    };
  }, [
    comparisonIndexName,
    indexName,
    comparisonFieldName,
    fieldName,
    comparisonRows,
    byDayId,
    entityType,
    mainRows,
    lastSecondaryValue,
    getMetricTotalValue,
    aggregationFunctionForTotal,
    metricTypeDisplay,
    field,
    isComparisonDataCustom,
    comparisonPeriod,
    formatMetric,
    response,
    isLoading
  ]);
};
